// client/src/components/pages/LexiStar/Routes/AdvisoriesPanel.tsx (converted from .jsx, ST-00261)
//
// Route advisories (FR-ROUT-006): a time-boxed notice — closure, protest,
// accident, roadworks — optionally with a temporary alternate corridor that
// counts as compliant while it is valid. The extra distance re-baselines trip
// budgets (FR-DEV-006); left empty, the server works it out from the alternate.
// Retracting ends an advisory early; nothing is deleted.
//
// ST-00261: publishing tells the driver of every live trip on the route (one
// message per trip; a scheduled advisory when it starts, a trip dispatched later
// once its trip assignment went out). Each advisory shows who was told, whether
// the message went out, and who answered Understood (`advisory.notices`).

import React, { useState } from 'react';
import { FaExclamationTriangle, FaMagic, FaMapMarkerAlt } from 'react-icons/fa';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { formatAppDateTime } from '../../../../utils/dateUtils';
import { createAdvisory, retractAdvisory, generateRoute } from '../../../../services/lexistarRoutes';
import { CORRIDOR_MIN_M, formatKm } from './routeGeometry';
import type { AdvisoryNotices, AdvisoryNoticeTrip, RouteAdvisory } from '../../../../types/lexistar-routes';

type AdvisoryState = NonNullable<RouteAdvisory['effective_state']>;

const STATE_STYLES: Record<AdvisoryState, string> = {
  active: 'bg-orange-50 text-orange-800 ring-orange-200',
  scheduled: 'bg-blue-50 text-blue-700 ring-blue-200',
  expired: 'bg-slate-100 text-slate-500 ring-slate-200',
  retracted: 'bg-slate-100 text-slate-500 ring-slate-200',
};
const STATE_LABELS: Record<AdvisoryState, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
  retracted: 'Retracted',
};

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  sms: 'SMS',
  fake: 'simulator',
};

const pad = (n: number): string => String(n).padStart(2, '0');
const toLocalValue = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const localToIso = (s: string): string | null => {
  const d = s ? new Date(s) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
};
const apiError = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: string } } } | null)?.response?.data?.error || fallback;

const inputClass =
  'w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** "whatsapp:no_consent,telegram:no_address" -> plain words for the control room. */
export function noticeReason(error: string): string {
  const words: Record<string, string> = {
    no_consent: 'no opt-in',
    no_address: 'no number or chat',
    template_not_approved: 'template not approved yet and no message from the driver in 24 hours',
    opted_out: 'opted out',
    over_budget: 'over budget',
    no_template: 'no template',
    inactive_recipient: 'driver inactive',
    // ST-00267: the organisation's Driver messages switch (Notification Rules → Settings)
    driver_messages_off: 'driver messages are switched off for your organisation',
  };
  return error
    .split(',')
    .map((p) => {
      const [channel, reason] = p.split(':');
      if (!reason) return words[p] || p;
      return `${CHANNEL_LABELS[channel || ''] || channel} ${words[reason] || reason}`;
    })
    .join('; ');
}

/**
 * One trip's status: acknowledged / sent, delivered or read (by which channel; "no reply asked"
 * when it went out as information only, while the driver was answering another question) / not
 * delivered (why) / not sent (no message although the sweep retried, or 5 minutes on) / queued.
 */
export function noticeStatus(t: AdvisoryNoticeTrip): { label: string; tone: 'ok' | 'bad' | 'neutral' } {
  if (t.acknowledged_at) return { label: `Acknowledged ${formatAppDateTime(t.acknowledged_at)}`, tone: 'ok' };
  const d = t.delivery;
  if (!d) return t.not_sent ? { label: 'Not sent', tone: 'bad' } : { label: 'Queued', tone: 'neutral' };
  const via = d.channel ? ` by ${CHANNEL_LABELS[d.channel] || d.channel}` : '';
  const info = t.ack_requested === false ? ' · no reply asked' : '';
  switch (d.state) {
    case 'suppressed':
    case 'failed':
      return { label: `Not delivered${d.error ? `: ${noticeReason(d.error)}` : ''}`, tone: 'bad' };
    case 'read':
      return { label: `Read${via}${info}`, tone: 'neutral' };
    case 'delivered':
      return { label: `Delivered${via}${info}`, tone: 'neutral' };
    case 'sent':
      return { label: `Sent${via}${info}`, tone: 'neutral' };
    default:
      return { label: `Queued${via}`, tone: 'neutral' };
  }
}

const TONES: Record<'ok' | 'bad' | 'neutral', string> = {
  ok: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  bad: 'bg-red-50 text-red-800 ring-red-200',
  neutral: 'bg-slate-100 text-slate-600 ring-slate-200',
};

/** Who was told about an advisory, delivered or not, acknowledged or not (null before migration 074). */
function NoticesSummary({ advisory }: { advisory: RouteAdvisory }) {
  const [open, setOpen] = useState(false);
  const notices: AdvisoryNotices | null | undefined = advisory.notices;
  if (!notices) return null;
  const state = advisory.effective_state || advisory.state;
  if (notices.notified === 0) {
    return (
      <p className="mt-1 text-xs text-slate-500">
        {state === 'scheduled'
          ? 'The drivers on this route are told when it starts.'
          : state === 'active'
            ? 'No live trip on this route: drivers dispatched onto it are told.'
            : 'No driver was told.'}
      </p>
    );
  }
  const listId = `advisory-notices-${advisory.id}`;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        className="text-xs text-slate-600 underline underline-offset-2 hover:text-slate-900"
      >
        {`${notices.notified} driver${notices.notified === 1 ? '' : 's'} told · ${notices.acknowledged} acknowledged`}
      </button>
      {open && (
        <ul id={listId} className="mt-1 space-y-1" aria-label="Drivers told">
          {notices.trips.map((t) => {
            const s = noticeStatus(t);
            return (
              <li key={t.trip_id} className="flex flex-wrap items-center justify-between gap-1 text-xs">
                <span className="text-slate-700">
                  {t.trip_reference || 'Trip'}
                  {t.driver_name ? ` · ${t.driver_name}` : ''}
                  {t.advisory_version > 1 ? ` · update ${t.advisory_version}` : ''}
                  {t.trigger === 'driver_changed' ? ' · new driver' : ''}
                </span>
                <span
                  className={`inline-flex px-1.5 py-0.5 text-[11px] ring-1 ring-inset ${
                    s.tone === 'bad' ? 'rounded-md' : 'rounded-full'
                  } ${TONES[s.tone]}`}
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export interface AdvisoriesPanelProps {
  route: { id: string; corridor_m?: number | null };
  lane: { from_location_id: number; to_location_id: number };
  advisories: RouteAdvisory[];
  canChange: boolean;
  altVia: number[][];
  onAltViaChange: (via: number[][]) => void;
  addingAltVia: boolean;
  onToggleAddingAltVia: () => void;
  altLine: number[][] | null;
  onAltLine: (coordinates: number[][] | null) => void;
  onChanged: () => void;
}

export default function AdvisoriesPanel({
  route,
  lane,
  advisories,
  canChange,
  altVia,
  onAltViaChange,
  addingAltVia,
  onToggleAddingAltVia,
  altLine,
  onAltLine,
  onChanged,
}: AdvisoriesPanelProps) {
  const now = new Date();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [validFrom, setValidFrom] = useState(toLocalValue(now));
  const [validTo, setValidTo] = useState(toLocalValue(new Date(now.getTime() + 24 * 3600 * 1000)));
  const [withAlt, setWithAlt] = useState(false);
  const [altCorridorM, setAltCorridorM] = useState<number>(route.corridor_m || 300);
  const [extraKm, setExtraKm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setOpen(false);
    setReason('');
    setWithAlt(false);
    setExtraKm('');
    onAltLine(null);
    onAltViaChange([]);
  };

  const generateAlt = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await generateRoute({
        from_location_id: lane.from_location_id,
        to_location_id: lane.to_location_id,
        via: altVia,
      });
      onAltLine(res.geometry.coordinates);
    } catch (err) {
      setError(apiError(err, 'Could not generate the alternate.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('Say why: drivers and controllers see this reason.');
    const from = localToIso(validFrom);
    const to = localToIso(validTo);
    if (!to) return setError('Choose until when the advisory applies.');
    if (withAlt && !altLine) return setError('Generate the alternate corridor, or untick it.');
    setBusy(true);
    try {
      await createAdvisory(route.id, {
        reason: reason.trim(),
        valid_from: from || undefined,
        valid_to: to,
        alt_corridor: withAlt
          ? {
              geometry: { type: 'LineString', coordinates: altLine },
              corridor_m: Number(altCorridorM),
            }
          : null,
        extra_km: extraKm === '' ? null : Number(extraKm),
      });
      reset();
      onChanged();
    } catch (err) {
      setError(apiError(err, 'Could not publish the advisory.'));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const retract = async (a: RouteAdvisory) => {
    setError(null);
    try {
      await retractAdvisory(a.id);
      onChanged();
    } catch (err) {
      setError(apiError(err, 'Could not retract the advisory.'));
    }
  };

  return (
    <div className="space-y-3">
      {canChange && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-orange-800 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100"
        >
          <FaExclamationTriangle size={12} aria-hidden="true" /> New advisory
        </button>
      )}

      {open && (
        <form
          onSubmit={submit}
          className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-3"
          aria-label="New advisory"
        >
          <label className="block text-xs text-slate-600">
            Reason
            <input
              className={inputClass}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="R555 closed at Witbank for roadworks"
            />
          </label>
          <div>
            <p className="text-xs text-slate-600 mb-1">Valid</p>
            <UnifiedDateTimeRangePicker
              startValue={validFrom}
              endValue={validTo}
              onStartChange={setValidFrom}
              onEndChange={setValidTo}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={withAlt}
              onChange={(e) => {
                setWithAlt(e.target.checked);
                if (!e.target.checked) {
                  onAltLine(null);
                  onAltViaChange([]);
                }
              }}
            />
            Temporary alternate corridor
          </label>
          {withAlt && (
            <div className="space-y-2 pl-6">
              <p className="text-xs text-slate-600">
                Add via points on the detour, then generate. Trips inside it are compliant while the
                advisory is valid.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onToggleAddingAltVia}
                  aria-pressed={addingAltVia}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                    addingAltVia
                      ? 'bg-orange-600 text-white'
                      : 'bg-surface border border-slate-200 text-slate-700'
                  }`}
                >
                  <FaMapMarkerAlt size={10} aria-hidden="true" />
                  {addingAltVia ? 'Done adding' : `Via points (${altVia.length})`}
                </button>
                <button
                  type="button"
                  onClick={generateAlt}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-orange-600 text-white disabled:opacity-60"
                >
                  <FaMagic size={10} aria-hidden="true" /> Generate alternate
                </button>
              </div>
              <label className="block text-xs text-slate-600">
                Corridor width ({altCorridorM} m)
                <input
                  type="range"
                  min={CORRIDOR_MIN_M}
                  max={2000}
                  step={50}
                  value={altCorridorM}
                  onChange={(e) => setAltCorridorM(Number(e.target.value))}
                  className="w-full accent-orange-600"
                />
              </label>
              <label className="block text-xs text-slate-600">
                Extra distance (km) — empty: worked out from the alternate
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={extraKm}
                  onChange={(e) => setExtraKm(e.target.value)}
                />
              </label>
            </div>
          )}
          <p className="text-xs text-slate-500">
            The driver of every live trip on this route gets it on WhatsApp, Telegram or SMS and is
            asked to press Understood.
          </p>
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="px-3 py-1.5 text-sm text-slate-600 bg-surface border border-slate-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-60"
            >
              Publish advisory
            </button>
          </div>
        </form>
      )}

      {!open && error && (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}

      {advisories.length === 0 ? (
        <p className="text-sm text-slate-600">No advisories on this route.</p>
      ) : (
        <ul className="space-y-2">
          {advisories.map((a) => {
            const state: AdvisoryState = a.effective_state || a.state;
            return (
              <li key={a.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900">{a.reason}</p>
                  <span
                    className={`flex-shrink-0 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${
                      STATE_STYLES[state] || STATE_STYLES.expired
                    }`}
                  >
                    {STATE_LABELS[state] || state}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {formatAppDateTime(a.valid_from)} → {formatAppDateTime(a.valid_to)}
                  {a.issued_by ? ` · ${a.issued_by}` : ''}
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {a.alt_corridor
                    ? `Alternate corridor ${a.alt_corridor.corridor_m} m wide`
                    : 'No alternate corridor'}
                  {a.extra_km != null ? ` · +${formatKm(a.extra_km)}` : ''}
                </p>
                <NoticesSummary advisory={a} />
                {canChange && a.state === 'active' && state !== 'expired' && (
                  <button
                    type="button"
                    onClick={() => retract(a)}
                    className="mt-2 px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    Retract
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
